import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { CollectionUtils } from '~src/Collections/utils/CollectionUtils';
import { RecycleBinUtils } from '~src/recycleBin/utils/RecycleBinUtils';
import { AuthLevel, getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { RouterError } from '../../../../bootstrap/routerHelpers/RouterError';
import { fetchResourceActions } from '../../../../resourceActions/actions/ResourceActions';
import { canPerformAnyAction, getActionsByResourceType, isFetchingResourceActions as selectIsFetchingResourceActions } from '../../../../resourceActions/selectors/ResourceActionsSelectors';
import { LegacySiteUserSubspaceName, legacySiteUserSubspaceNameToExplorableItemType, SiteUserSubspaceName } from '../../../../spacesPlatform/state/SpaceInfo';
import { SiteUserSpaceProvider, SiteUserSpaceProviderProps } from './SiteUserSpaceProvider';

// The properties here must match the strings in the Routes' path below
export interface SiteUserSpaceRouteParams {
  readonly domain: string;
  readonly username: string;
}
export interface SiteUserRouteComponentProps extends RouteComponentProps<SiteUserSpaceRouteParams> {}

export interface SiteUserSpaceRouterOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly provider: (subspaceName: SiteUserSubspaceName, providerProps: SiteUserSpaceComponentWithRouterProps) => (props: SiteUserRouteComponentProps) => JSX.Element;
}

export interface SiteUserSpaceRouterStateProps {
  readonly canConfigureUsers: boolean;
  readonly isFetchingResourceActions: boolean;
}

export interface SiteUserSpaceRouterDispatchProps {
  readonly fetchResourceActions: () => void;
}

export type SiteUserSpaceRouterProps = SiteUserSpaceRouterOwnProps & SiteUserSpaceRouterStateProps & SiteUserSpaceRouterDispatchProps;

export class SiteUserSpaceRouterComponent extends React.Component<SiteUserSpaceRouterProps> {
  private server: SessionManagementService;

  public constructor(props: SiteUserSpaceRouterProps) {
    super(props);
    this.server = getSessionManagementServiceInstance();
  }

  public componentDidMount() {
    // Fetch resource-level actions when admin profiles is enabled.
    // This runs before any sub-route renders, ensuring resource actions are available
    // for the settings route guard even when navigating directly to the settings URL.
    if (this.server.isAdminProfilesEnabled()) {
      this.props.fetchResourceActions();
    }
  }

  public render() {
    return (
      // Since the "/" is allowed in usernames historically back to early Tableau Server versions, we want to support
      // routing in these cases. However, by default, React Router doesn't handle this character in a way we want.
      // The "+" notation is a way to make the matching greedy, whereas without it we would break at all "/" characters,
      // which is the way React Router natively interprets matches.
      // See:
      // https://github.com/ReactTraining/react-router/issues/313
      // and
      // https://stackoverflow.com/questions/56133182/allow-slashes-in-route-parameter-on-react
      <Route path={decodeURIComponent(this.props.urls.userPath(':username+', ':domain'))} render={() => (
        <Switch>
          <Route exact
            path={decodeURIComponent(this.props.urls.userContentPath(':username+', ':domain'))}
            render={this.props.provider('content', this.props)} />
          <Route exact
            path={decodeURIComponent(this.props.urls.userProjectsPath(':username+', ':domain'))}
            render={this.redirectToContentSubspace('projects')} />
          <Route exact
            path={decodeURIComponent(this.props.urls.userWorkbooksPath(':username+', ':domain'))}
            render={this.redirectToContentSubspace('workbooks')} />
          <Route exact
            path={decodeURIComponent(this.props.urls.userViewsPath(':username+', ':domain'))}
            render={this.redirectToContentSubspace('views')} />
          <Route exact
            path={decodeURIComponent(this.props.urls.userDatasourcesPath(':username+', ':domain'))}
            render={this.redirectToContentSubspace('datasources')} />
          <Route exact
            path={decodeURIComponent(this.props.urls.userFlowsPath(':username+', ':domain'))}
            render={this.redirectToContentSubspace('flows')} />
          <Route exact
            path={decodeURIComponent(this.props.urls.userDataAlertsPath(':username+', ':domain'))}
            render={this.renderProtectedSubspace(this.server, 'alerts')} />
          <Route exact
            path={decodeURIComponent(this.props.urls.userCustomViewsPath(':username+', ':domain'))}
            render={this.renderProtectedSubspace(this.server, 'customViews')} />
          <Route exact
            path={decodeURIComponent(this.props.urls.userSubscriptionsPath(':username+', ':domain'))}
            render={this.renderProtectedSubspace(this.server, 'subscriptions')} />
          {
            CollectionUtils.checkCollectionSiteSetting() &&
            <Route exact
              path={decodeURIComponent(this.props.urls.userCollectionsPath(':username+', ':domain'))}
              render={this.props.provider('collections', this.props)} />
          }
          {
            RecycleBinUtils.isRecycleBinEnabled() &&
            <Route exact
              path={decodeURIComponent(this.props.urls.userRecycleBinPath(':username+', ':domain'))}
              render={this.props.provider('recyclebin', this.props)}
              />
          }
          <Route exact
            path={decodeURIComponent(this.props.urls.userPackagesPath(':username+', ':domain'))}
            render={this.renderProtectedSubspace(this.server, 'packages')} />
          <Route exact
            path={decodeURIComponent(this.props.urls.userPackageDetailsPath(':username+', ':domain', ':luid'))}
            render={this.renderProtectedSubspace(this.server, 'packages')} />
          <Route exact
            path={decodeURIComponent(this.props.urls.userSettingsPath(':username+', ':domain'))}
            render={this.renderSettingsSubspace(this.server)} />
          <Route exact path={decodeURIComponent(this.props.urls.userPath(':username+', ':domain'))} render={({ match }) => (
            <Redirect to={decodeURIComponent(this.props.urls.userContentPath(match.params.username, match.params.domain))} />
          )} />
          <Route render={this.error} />
        </Switch>
      )} />
    );
  }

  private redirectToContentSubspace(specificContentType: LegacySiteUserSubspaceName): (props: SiteUserRouteComponentProps) => JSX.Element {
    return (props) => {
      let query = queryString.parse(props.location.search);
      query.contentType = legacySiteUserSubspaceNameToExplorableItemType(specificContentType);

      return <Redirect to={{
        pathname: this.props.urls.userContentPath(props.match.params.username, props.match.params.domain),
        search: queryString.stringify(query)
      }} />;
    };
  }

  private renderSettingsSubspace(server: SessionManagementService): (props: SiteUserRouteComponentProps) => JSX.Element {
    return (props) => {
      const username = decodeURIComponent(props.match.params.username);
      const isSelf = server.isSessionUserSelf(username, props.match.params.domain);
      if (isSelf || this.props.canConfigureUsers) {
        return this.props.provider('settings', this.props)(props);
      }
      // Defer the access decision while resource actions are still loading to avoid
      // a false negative on the first render before the API response arrives.
      if (this.props.isFetchingResourceActions) {
        return null;
      }
      return this.renderProtectedSubspace(this.server, 'settings')(props);
    };
  }

  private renderProtectedSubspace(server: SessionManagementService, subspace: SiteUserSubspaceName): (props: SiteUserRouteComponentProps) => JSX.Element {
    return (props) => {
      if (server.isUserAuthorized(AuthLevel.SelfOrAdmin, decodeURIComponent(props.match.params.username), props.match.params.domain)) {
        return this.props.provider(subspace, this.props)(props);
      }
      return this.error(props);
    };
  }

  private error = (_props: RouteComponentProps): JSX.Element => {
    return <RouterError errorType={'default'} urls={this.props.urls} />;
  };
}

export type SiteUserSpaceComponentWithRouterProps = Omit<SiteUserSpaceRouterOwnProps, 'provider'>;
export const provider = (subspaceName: SiteUserSubspaceName, providerProps: SiteUserSpaceComponentWithRouterProps): (props: SiteUserRouteComponentProps) => JSX.Element => {
  return (props: SiteUserRouteComponentProps): JSX.Element => {
    const spaceProps: SiteUserSpaceProviderProps = {
      routeLocationSearch: props.location.search,
      routeLocationPath: props.location.pathname,
      // Need to decode username as special characters like space, #, etc are allowed for an username and are decoded in the url
      userName: decodeURIComponent(props.match.params.username),
      domainName: props.match.params.domain,
      siteName: providerProps.siteName,
      subspaceName,
      urls: providerProps.urls,
    };
    return <SiteUserSpaceProvider {...spaceProps} />;
  };
};

type SiteUserSpaceRouterContainerOwnProps = Omit<SiteUserSpaceRouterOwnProps, 'provider'>;

const mapStateToProps: MapStateToProps<SiteUserSpaceRouterStateProps, SiteUserSpaceRouterContainerOwnProps> = (appState) => {
  const isFetching = selectIsFetchingResourceActions(appState);
  const actionsByType = getActionsByResourceType(appState);
  const isAdminProfilesEnabled = getSessionManagementServiceInstance().isAdminProfilesEnabled();
  // Treat as loading if: actively fetching, OR if isAdminProfilesEnabled and we have
  // never fetched yet (empty map, no error). This prevents a false-negative access denial
  // on the very first render before componentDidMount has dispatched the fetch.
  const neverFetched = isAdminProfilesEnabled && !isFetching && actionsByType.size === 0;
  return {
    canConfigureUsers: isAdminProfilesEnabled ? canPerformAnyAction(appState, 'User', ['MODIFY_USERS']) : false,
    isFetchingResourceActions: isFetching || neverFetched,
  };
};

const mapDispatchToProps: MapDispatchToProps<SiteUserSpaceRouterDispatchProps> = (dispatch) => bindActionCreators({
  fetchResourceActions,
}, dispatch);

const ConnectedSiteUserSpaceRouterComponent = connect(mapStateToProps, mapDispatchToProps)(SiteUserSpaceRouterComponent);

const SiteUserSpaceComponent = (props: SiteUserSpaceRouterContainerOwnProps) => {
  return <ConnectedSiteUserSpaceRouterComponent {...props} provider={provider} />;
};

export const SiteUserSpaceRouter: React.FunctionComponent<SiteUserSpaceRouterContainerOwnProps> = SiteUserSpaceComponent;
